/**
 * Lift2Fit — the lifter's session reminder and schedule editor.
 *
 * The programme is undated by design and the "when" layer is ownership-aware
 * (feature 0023):
 *
 *   * a **coach-owned** block's date comes from the coach's calendar. A booked
 *     slot is shown with the programme day it maps to; with nothing booked the
 *     card and the editor route to the coach's available slots for the pack the
 *     lifter subscribes to — never a free weekday setter;
 *   * a **self-authored** block keeps the 0022 behaviour: the lifter's own
 *     weekly pattern, repeating;
 *   * otherwise an honest "not set", with the paths out that fit the ownership.
 *
 * `UpcomingSessionCard` sits next to Coach alerts on the dashboard and shows
 * ONLY the next one. `ScheduleEditorScreen` (`#/schedule`) sets the whole week in
 * one action for self-authored days and books coach-owned days from the coach's
 * calendar.
 *
 * This card is deliberately NOT the dashboard's undated "Next session" panel:
 * that one answers "what should I train next"; this one answers "when".
 */
import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type CoachPackSubscription,
  type CrmSlot,
  type LifterSchedule,
  type NextSession,
  type ScheduleInput,
} from '@/lib/api';
import { cn, pluralize } from '@/lib/format';
import { ArrowRightIcon, CheckIcon, SyncIcon, TimerIcon } from '@/lib/icons';
import { AppLinkButton, EmptyState, LiveMessage, LoadingPanel, Panel, StatusChip } from './components';
import { useAsync } from './hooks';
import { Link } from './router';

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/** `HH:mm` → minutes from local midnight, or null for an empty/invalid value. */
function timeToMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/** Minutes from local midnight → `HH:mm`, or '' when there is no time. */
function minutesToTime(value: number | null): string {
  if (value === null) return '';
  const hours = String(Math.floor(value / 60) % 24).padStart(2, '0');
  const minutes = String(value % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

interface Availability {
  hasCoach: boolean;
  hasUpcomingSlot: boolean;
  /** The lifter's active pack subscription with this coach, when there is one. */
  subscription: CoachPackSubscription | null;
  /**
   * The earliest open slot for the SUBSCRIBED pack, so a coach-managed lifter
   * sees a real, bookable slot and not just a link.
   */
  nextPackSlot: CrmSlot | null;
}

/** "Wed 21 Oct · 09:00" in the lifter's locale, or '' when unreadable. */
function slotMomentLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A slot the lifter can still take a place in. */
function isBookable(slot: CrmSlot): boolean {
  return slot.status === 'open' && slot.booked < slot.capacity;
}

/* ---------------------------------------------------------------------------
   The dashboard card — only the next one
   ------------------------------------------------------------------------ */

export function UpcomingSessionCard() {
  const state = useAsync(() => api.nextSession(), []);
  // A booking path is offered only when there is a human coach AND a slot on
  // their calendar. The dashboard's `coach` key is the AI premium panel, not a
  // link, so the availability read is the source of truth.
  const availability = useAsync(
    () =>
      api.crmCoachAvailability(
        new Date().toISOString(),
        new Date(Date.now() + 28 * 86_400_000).toISOString(),
      ),
    [],
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next: NextSession =
    state.data ?? {
      kind: 'none',
      startsAt: null,
      hasTime: false,
      dayLabel: null,
      planName: null,
      source: 'none',
      managedBy: null,
    };
  const slots = availability.data?.data ?? [];
  const subscription = availability.data?.subscription ?? null;
  const info: Availability = {
    hasCoach: Boolean(availability.data?.coach),
    hasUpcomingSlot: slots.some(isBookable),
    subscription,
    nextPackSlot:
      slots
        .filter((slot) => isBookable(slot) && subscription !== null && slot.packId === subscription.packId)
        .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0] ?? null,
  };

  const run = async (action: () => Promise<{ problem?: { detail?: string; title?: string } }>) => {
    setBusy(true);
    setNotice(null);
    const result = await action();
    setBusy(false);
    if (result.problem) {
      setNotice(result.problem.detail ?? result.problem.title ?? 'That did not work.');
      return;
    }
    state.reload();
  };

  // A booked slot is always the coach's; a self-set pattern is always the
  // lifter's. `managedBy` decides the actions, so the two-active-block case
  // routes to whoever owns the shown session.
  const coachBooked = next.kind === 'booked';
  const selfScheduled = next.kind === 'scheduled' && next.managedBy !== 'coach';
  const coachUnbooked = next.kind !== 'booked' && next.managedBy === 'coach';

  const tone = coachBooked ? 'info' : selfScheduled ? 'accent' : coachUnbooked ? 'warning' : 'neutral';
  const stateWord = coachBooked
    ? 'Booked'
    : selfScheduled
      ? 'Scheduled'
      : coachUnbooked
        ? 'Not booked'
        : 'Not set';
  const stateIcon = next.kind === 'none' && !coachUnbooked ? <SyncIcon /> : <TimerIcon />;

  return (
    <Panel
      title="Upcoming session"
      subtitle="When your next session is — your reminder, not the workout."
      testId="upcoming-session-card"
      actions={
        <StatusChip tone={tone} icon={stateIcon}>
          {stateWord}
        </StatusChip>
      }
    >
      {state.loading && !state.data ? (
        <LoadingPanel label="Loading your next session" rows={2} />
      ) : coachBooked ? (
        <div className="flex flex-col gap-4" data-testid="upcoming-booked">
          <span className="font-display text-[22px] font-black uppercase leading-none text-ink">
            {next.dayLabel}
          </span>
          {next.planDayLabel ? (
            <p className="text-[14px] text-ink" data-testid="upcoming-booked-day">
              <span className="font-bold">{next.planDayLabel}</span>
              {next.planDayFocus?.length ? (
                <span className="text-ink-muted"> · {next.planDayFocus.join(' · ')}</span>
              ) : null}
              <span className="text-ink-muted"> — the programme day this slot maps to.</span>
            </p>
          ) : null}
          <p className="text-[14px] text-ink">
            <span className="font-bold">{next.packName ?? 'Coaching session'}</span>
            {next.coachName ? <> with {next.coachName}</> : null}.{' '}
            <span className="text-ink-muted">Your coach's diary owns this date.</span>
          </p>
          {next.planDayDerived ? (
            <p className="text-[12px] text-ink-muted">
              The booked date is the authority; the programme day is the rotation's best guess, so
              skipping or swapping a session can move it.
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <AppLinkButton to="/my-coach/booking" variant="outline">
              Open booking
            </AppLinkButton>
            {next.bookingId ? (
              <button
                type="button"
                data-testid="upcoming-cancel-booking"
                disabled={busy}
                onClick={() => void run(() => api.crmCancelBooking(next.bookingId as string))}
                className="inline-flex h-11 items-center justify-center border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
              >
                Cancel booking
              </button>
            ) : null}
          </div>
        </div>
      ) : selfScheduled ? (
        <div className="flex flex-col gap-4" data-testid="upcoming-scheduled">
          <span className="font-display text-[22px] font-black uppercase leading-none text-ink">
            {next.dayLabel}
          </span>
          <p className="text-[14px] text-ink">
            <span className="font-bold">{next.planName ?? 'Your programme'}</span>{' '}
            <span className="text-ink-muted">— your own schedule, repeating weekly.</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <AppLinkButton to="/schedule" variant="outline">
              Change schedule
            </AppLinkButton>
            {next.scheduleId ? (
              <button
                type="button"
                data-testid="upcoming-skip"
                disabled={busy}
                onClick={() => void run(() => api.skipScheduleEntry(next.scheduleId as string))}
                className="inline-flex h-11 items-center justify-center border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
              >
                Skip this one
              </button>
            ) : null}
            <button
              type="button"
              data-testid="upcoming-stop"
              disabled={busy}
              onClick={() => void run(() => api.stopSchedule())}
              className="inline-flex h-11 items-center justify-center border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
            >
              Stop repeating
            </button>
          </div>
        </div>
      ) : coachUnbooked ? (
        <div className="flex flex-col gap-4" data-testid="upcoming-none">
          <div className="flex flex-col gap-4" data-testid="upcoming-coach-unbooked">
            <EmptyState
              title="No session booked"
              body={
                subscription
                  ? `Your coach's calendar owns this block. Book a ${subscription.packName} slot — the date and time come from the slot.`
                  : "Your coach's calendar owns this block. Book a slot — the date and time come from the slot."
              }
              icon={<TimerIcon className="h-8 w-8 text-accent" />}
            />
            <div className="flex flex-wrap items-center gap-3">
              <AppLinkButton
                to="/my-coach/booking"
                variant="primary"
                data-testid="upcoming-book-slot"
              >
                Book a slot
                <ArrowRightIcon />
              </AppLinkButton>
              {info.nextPackSlot ? (
                <span className="text-[12px] text-ink-muted" data-testid="upcoming-coach-next-slot">
                  Next available for {subscription?.packName ?? 'your pack'}:{' '}
                  {slotMomentLabel(info.nextPackSlot.startsAt)}.
                </span>
              ) : info.hasCoach ? (
                <span className="text-[12px] text-ink-muted" data-testid="upcoming-coach-no-slot">
                  Your coach has no open slot in the next 28 days yet.
                </span>
              ) : (
                <span className="text-[12px] text-ink-muted" data-testid="upcoming-coach-no-slot">
                  Ask your coach to add availability, then book a slot.
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="upcoming-none">
          <EmptyState
            title="No session scheduled"
            body="The programme has no dates on it until you set them. Pick the days you train, or book a slot with your coach."
            icon={<SyncIcon className="h-8 w-8 text-accent" />}
          />
          <div className="flex flex-wrap items-center gap-3">
            <AppLinkButton to="/schedule" variant="outline" data-testid="upcoming-set-days">
              Set your days
              <ArrowRightIcon />
            </AppLinkButton>
            {info.hasCoach && info.hasUpcomingSlot ? (
              <AppLinkButton to="/my-coach/booking" variant="outline" data-testid="upcoming-book-slot">
                Book a slot with your coach
              </AppLinkButton>
            ) : info.hasCoach ? (
              <span className="text-[12px] text-ink-muted">
                Your coach has no open slot in the next 28 days yet.
              </span>
            ) : (
              <span className="text-[12px] text-ink-muted" data-testid="upcoming-self-managed">
                Self-managed — set your own days, or{' '}
                <Link to="/my-coach" className="underline hover:text-accent">
                  connect a coach
                </Link>
                .
              </span>
            )}
          </div>
        </div>
      )}

      {state.error && next.kind === 'none' ? (
        <p className="text-[12px] text-warning">
          Your reminder could not be read from the server just now.
        </p>
      ) : null}
      <LiveMessage message={notice} tone="danger" />
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   The schedule editor — the whole week in one action
   ------------------------------------------------------------------------ */

interface DraftDay {
  weekday: number | null;
  time: string;
}

type Draft = Record<string, DraftDay>;

function draftFrom(schedule: LifterSchedule): Draft {
  const draft: Draft = {};
  for (const day of schedule.days) {
    draft[day.planDayId] = {
      weekday: day.weekday,
      time: minutesToTime(day.startMinute),
    };
  }
  return draft;
}

export function ScheduleEditorScreen() {
  const state = useAsync(() => api.schedule(), []);
  const [schedule, setSchedule] = useState<LifterSchedule | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [until, setUntil] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bookingBusy, setBookingBusy] = useState<string | null>(null);
  // A coach-owned day has no weekday: its date comes from the coach's calendar.
  // The editor reads the same availability the booking tab does and puts the
  // subscribed pack's open slots here, so the setter sets a real slot.
  const availability = useAsync(
    () =>
      api.crmCoachAvailability(
        new Date().toISOString(),
        new Date(Date.now() + 56 * 86_400_000).toISOString(),
      ),
    [],
  );

  useEffect(() => {
    if (!state.data) return;
    setSchedule(state.data);
    setDraft(draftFrom(state.data));
    setUntil(state.data.repeatUntil ?? '');
  }, [state.data]);

  const planGroups = useMemo(() => {
    const groups = new Map<string, { planName: string; coachOwned: boolean; dayIds: string[] }>();
    for (const day of schedule?.days ?? []) {
      const group = groups.get(day.planId) ?? {
        planName: day.planName,
        coachOwned: day.coachOwned,
        dayIds: [],
      };
      group.dayIds.push(day.planDayId);
      groups.set(day.planId, group);
    }
    return [...groups.entries()];
  }, [schedule]);

  const coachDays = (schedule?.days ?? []).filter((day) => day.coachOwned);
  const selfDays = (schedule?.days ?? []).filter((day) => !day.coachOwned);
  const coachOwnedDayIds = new Set(coachDays.map((day) => day.planDayId));
  const coachSubscription = availability.data?.subscription ?? null;
  // Only the subscribed pack's slots are the lifter's to book (migration 0020).
  const coachSlots = (availability.data?.data ?? [])
    .filter(
      (slot) =>
        slot.packId !== null &&
        slot.packId === coachSubscription?.packId &&
        slot.status === 'open',
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));

  const configured = selfDays.filter(
    (day) => (draft[day.planDayId]?.weekday ?? null) !== null,
  ).length;
  const totalDays = selfDays.length;
  const allSet = totalDays > 0 && configured === totalDays;

  const bookCoachSlot = async (slot: CrmSlot) => {
    setBookingBusy(slot.slotId);
    setNotice(null);
    const result = await api.crmBookMyPlace(slot.slotId);
    setBookingBusy(null);
    if (result.problem || !result.data) {
      setNotice(result.problem?.detail ?? result.problem?.title ?? 'That slot was not booked.');
      return;
    }
    availability.reload();
  };

  const setDay = (planDayId: string, patch: Partial<DraftDay>) => {
    setDraft((current) => {
      const base: DraftDay = current[planDayId] ?? { weekday: null, time: '' };
      return { ...current, [planDayId]: { ...base, ...patch } };
    });
    setSavedAt(null);
  };

  const apply = (result: { data: LifterSchedule | null; problem?: { detail?: string; title?: string } }) => {
    setBusy(false);
    if (result.problem) {
      setNotice(result.problem.detail ?? result.problem.title ?? 'That did not save.');
      return false;
    }
    if (result.data) {
      setSchedule(result.data);
      setDraft(draftFrom(result.data));
      setUntil(result.data.repeatUntil ?? '');
    }
    setNotice(null);
    setSavedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    return true;
  };

  const save = async () => {
    const input: ScheduleInput = {
      days: Object.entries(draft)
        .filter(([planDayId, day]) => day.weekday !== null && !coachOwnedDayIds.has(planDayId))
        .map(([planDayId, day]) => ({
          planDayId,
          weekday: day.weekday as number,
          startMinute: timeToMinutes(day.time),
        })),
      repeatUntil: until === '' ? null : until,
    };
    if (input.days.length === 0) {
      setNotice('Pick at least one day — or clear the schedule to go back to "not set".');
      return;
    }
    setBusy(true);
    apply(await api.saveSchedule(input));
  };

  const stop = async () => {
    setBusy(true);
    apply(await api.stopSchedule());
  };

  const clear = async () => {
    setBusy(true);
    const result = await api.clearSchedule();
    setBusy(false);
    if (result.problem) {
      setNotice(result.problem.detail ?? result.problem.title ?? 'That did not clear.');
      return;
    }
    const reloaded = await api.schedule();
    setSchedule(reloaded.data);
    setDraft(reloaded.data ? draftFrom(reloaded.data) : {});
    setUntil('');
    setNotice(null);
    setSavedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
  };

  if (state.loading && !schedule) {
    return (
      <div className="flex flex-col gap-6">
        <LoadingPanel label="Loading your week" rows={4} />
      </div>
    );
  }

  if (!schedule) {
    return (
      <EmptyState
        title="We could not read your schedule"
        body={state.error ?? 'The API did not answer.'}
        icon={<TimerIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/dashboard" variant="outline">
            Back to the dashboard
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="schedule-editor">
      <header className="flex flex-col gap-3">
        <p className="text-label font-bold uppercase tracking-[0.08em] text-accent">Your training</p>
        <h1 className="font-display text-h2 font-black uppercase leading-none tracking-[-0.01em] text-ink md:text-h1">
          Your training week
        </h1>
        <p className="max-w-[68ch] text-[15px] text-ink-muted">
          Pick which programme day lands on which weekday. Set all your own days in one go — the
          pattern repeats every week until you stop it. A coach-owned block is different: its dates
          live on your coach's calendar, so you book a slot instead of choosing a weekday.
        </p>
      </header>

      {!schedule.active ? (
        <p className="border-l-2 border-warning bg-page-subtle px-3 py-2 text-[13px] text-ink" data-testid="schedule-not-set">
          Nothing is set right now. Choose your days below and save — or book a slot with your coach.
        </p>
      ) : null}

      {totalDays > 0 && !allSet ? (
        <p className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[13px] text-ink" data-testid="schedule-all-days-nudge">
          You have {pluralize(totalDays, 'programme day')}. Set all {totalDays} so the reminder knows
          your week — repeat once, not day by day.
        </p>
      ) : null}

      <Panel
        title="Repeat weekly"
        subtitle={`Times are ${schedule.timezone}.`}
        testId="schedule-week"
        actions={
          schedule.active ? (
            <StatusChip tone="accent" icon={<SyncIcon />}>
              Repeats weekly
            </StatusChip>
          ) : (
            <StatusChip tone="neutral" icon={<SyncIcon />}>
              Not repeating
            </StatusChip>
          )
        }
      >
        <div className="flex flex-col divide-y divide-hairline">
          {planGroups.map(([planId, group]) => (
            <div key={planId} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
                  {group.planName}
                </span>
                {group.coachOwned ? <StatusChip tone="info">Your coach's block</StatusChip> : null}
              </span>
              {group.coachOwned ? (
                <div
                  data-testid={`schedule-coach-block-${planId}`}
                  className="flex flex-col gap-3 border border-hairline bg-surface p-3"
                >
                  <p className="text-[13px] text-ink">
                    Your coach's calendar owns when this block happens.{' '}
                    <span className="text-ink-muted">
                      Book a slot for your pack — the date comes from the slot, and the reminder maps
                      it back to the next due programme day.
                    </span>
                  </p>
                  <span className="flex flex-wrap items-center gap-2">
                    {coachDays
                      .filter((day) => day.planId === planId)
                      .map((day) => (
                        <StatusChip key={day.planDayId} tone="neutral">
                          {day.label}
                          {day.focus.length ? ` · ${day.focus.join(' · ')}` : ''}
                        </StatusChip>
                      ))}
                  </span>
                  {availability.loading && !availability.data ? (
                    <p className="text-[13px] text-ink-muted">Loading your coach's availability…</p>
                  ) : coachSubscription === null ? (
                    <p className="text-[13px] text-ink-muted" data-testid="schedule-coach-no-pack">
                      You do not hold an active coaching pack yet.{' '}
                      <Link to="/my-coach/discover" className="underline hover:text-accent">
                        Find a pack
                      </Link>{' '}
                      to book this block.
                    </p>
                  ) : coachSlots.length === 0 ? (
                    <p className="text-[13px] text-ink-muted" data-testid="schedule-coach-no-slots">
                      No open {coachSubscription.packName} slot in the next 8 weeks. Open booking to
                      see your coach's calendar.
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-1.5" data-testid="schedule-coach-slots">
                      {coachSlots.map((slot) => {
                        const mine = slot.places.length > 0;
                        return (
                          <li
                            key={slot.slotId}
                            data-testid={`schedule-coach-slot-${slot.slotId}`}
                            className="flex flex-wrap items-center justify-between gap-2 border border-hairline px-3 py-2"
                          >
                            <span className="flex min-w-0 flex-col">
                              <span className="text-[13px] font-bold text-ink">
                                {slotMomentLabel(slot.startsAt)}
                              </span>
                              <span className="text-[11px] text-ink-muted">
                                {coachSubscription.packName}
                                {slot.location ? ` · ${slot.location}` : ''} · {slot.booked}/
                                {slot.capacity} booked
                              </span>
                            </span>
                            {mine ? (
                              <StatusChip tone="success">Booked</StatusChip>
                            ) : slot.booked >= slot.capacity ? (
                              <StatusChip tone="danger">Full</StatusChip>
                            ) : (
                              <button
                                type="button"
                                data-testid={`schedule-book-slot-${slot.slotId}`}
                                disabled={bookingBusy !== null}
                                onClick={() => void bookCoachSlot(slot)}
                                className="inline-flex h-9 items-center justify-center border border-hairline px-3 text-[12px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
                              >
                                {bookingBusy === slot.slotId ? 'Booking…' : 'Book'}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <AppLinkButton to="/my-coach/booking" variant="outline" size="sm">
                    Open booking
                  </AppLinkButton>
                </div>
              ) : (
                (schedule.days ?? [])
                  .filter((day) => day.planId === planId)
                  .map((day) => {
                    const value = draft[day.planDayId] ?? { weekday: day.weekday, time: minutesToTime(day.startMinute) };
                    return (
                      <div
                        key={day.planDayId}
                        data-testid={`schedule-day-${day.planDayId}`}
                        className="flex flex-col gap-3 border border-hairline bg-surface p-3 sm:flex-row sm:items-end"
                      >
                        <label className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                            Programme day
                          </span>
                          <span className="text-[14px] font-bold text-ink">{day.label}</span>
                          {day.focus.length ? (
                            <span className="text-[12px] text-ink-muted">{day.focus.join(' · ')}</span>
                          ) : null}
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                            Day
                          </span>
                          <select
                            data-testid={`schedule-weekday-${day.planDayId}`}
                            className="h-11 border border-hairline bg-surface px-3 text-[13px] text-ink"
                            value={value.weekday === null ? '' : String(value.weekday)}
                            onChange={(event) =>
                              setDay(day.planDayId, {
                                weekday: event.target.value === '' ? null : Number(event.target.value),
                              })
                            }
                          >
                            <option value="">Not set</option>
                            {WEEKDAYS.map((label, index) => (
                              <option key={label} value={String(index + 1)}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                            Time (optional)
                          </span>
                          <input
                            type="time"
                            data-testid={`schedule-time-${day.planDayId}`}
                            className="h-11 border border-hairline bg-surface px-3 text-[13px] text-ink"
                            value={value.time}
                            onChange={(event) => setDay(day.planDayId, { time: event.target.value })}
                          />
                        </label>
                      </div>
                    );
                  })
              )}
            </div>
          ))}
          {(schedule.days ?? []).length === 0 ? (
            <EmptyState
              title="No programme days to schedule yet"
              body="Build a block first — then set which day happens when."
              icon={<TimerIcon className="h-8 w-8 text-accent" />}
              action={
                <AppLinkButton to="/programme" variant="outline">
                  Open My Programme
                </AppLinkButton>
              }
            />
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-hairline pt-4 sm:flex-row sm:items-end sm:justify-between">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
              Repeat until (optional)
            </span>
            <input
              type="date"
              data-testid="schedule-repeat-until"
              className="h-11 border border-hairline bg-surface px-3 text-[13px] text-ink"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value);
                setSavedAt(null);
              }}
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="schedule-save"
              disabled={busy || totalDays === 0}
              onClick={() => void save()}
              className={cn(
                'inline-flex h-11 items-center justify-center px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent transition-colors',
                busy || totalDays === 0 ? 'bg-accent opacity-50' : 'bg-accent hover:bg-accent-hover',
              )}
            >
              Save my week
            </button>
            {schedule.active ? (
              <button
                type="button"
                data-testid="schedule-stop"
                disabled={busy}
                onClick={() => void stop()}
                className="inline-flex h-11 items-center justify-center border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
              >
                Stop repeating
              </button>
            ) : null}
            <button
              type="button"
              data-testid="schedule-clear"
              disabled={busy}
              onClick={() => void clear()}
              className="inline-flex h-11 items-center justify-center px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink-muted transition-colors hover:text-accent disabled:opacity-50"
            >
              Clear schedule
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {savedAt ? (
            <span data-testid="schedule-saved">
              <StatusChip tone="success" icon={<CheckIcon />}>
                Saved {savedAt}
              </StatusChip>
            </span>
          ) : null}
          <Link to="/grid" className="text-[12px] underline hover:text-accent">
            Open The Grid
          </Link>
          <Link to="/my-coach/booking" className="text-[12px] underline hover:text-accent">
            Book a slot with your coach
          </Link>
        </div>
        <LiveMessage message={notice} tone="danger" />
      </Panel>
    </div>
  );
}
